import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { hu } from "date-fns/locale";
import {
  Phone, CalendarDays, Wrench, CircleDot, ShieldCheck, ReceiptText, Zap,
  MapPin, Clock, Star, Menu, X, ChevronRight, CheckCircle2, Gauge, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  BRAND, LOGO_URL, OPENING_HOURS, IMAGES, REAL_PHOTOS, SERVICES, SERVICE_OPTIONS, REVIEWS,
  getTimeSlots, isClosedDay,
} from "@/lib/data";

const ICONS = { Wrench, CircleDot };

function useOpenStatus() {
  const now = new Date();
  const dow = now.getDay();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const today = OPENING_HOURS.find((d) => d.dow === dow);
  if (!today || today.closed) return false;
  const end = dow === 5 ? 16 * 60 : 16 * 60 + 30;
  return minutes >= 8 * 60 && minutes <= end;
}

const NAV_LINKS = [
  { id: "rolunk", label: "Rólunk" },
  { id: "szolgaltatasok", label: "Szolgáltatások" },
  { id: "velemenyek", label: "Vélemények" },
  { id: "foglalas", label: "Időpontfoglalás" },
  { id: "kapcsolat", label: "Kapcsolat" },
];

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* ------------------------------------------------------------------ Navbar */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isOpen = useOpenStatus();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md bg-[#0B0D11]/90 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <button
            data-testid="nav-logo"
            onClick={() => scrollTo("hero")}
            className="flex items-center gap-3"
          >
            <img src={LOGO_URL} alt={BRAND.name} className="h-11 w-11 sm:h-14 sm:w-14 rounded-full object-cover" />
            <span className="font-display font-extrabold text-white text-base sm:text-lg leading-none tracking-tight hidden xs:block">
              Ady <span className="text-brand-red">Szerviz</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
              <span className={`h-2 w-2 rounded-full ${isOpen ? "bg-emerald-400" : "bg-red-500"}`} />
              <span className="text-xs font-mono text-slate-300">{isOpen ? "Nyitva" : "Zárva"}</span>
            </div>
            <a href={BRAND.phoneTel} data-testid="nav-call-btn">
              <Button className="bg-brand-red hover:bg-[#c11010] text-white font-semibold gap-2 rounded-full glow-red">
                <Phone className="h-4 w-4" />
                <span className="hidden sm:inline">Hívás most</span>
              </Button>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0B0D11]/98 backdrop-blur-md border-t border-white/10">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => { scrollTo(l.id); setOpen(false); }}
                className="text-left px-3 py-3 text-slate-200 font-medium border-b border-white/5"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

/* -------------------------------------------------------------- Mobile bar */
function MobileCallBar() {
  return (
    <div
      data-testid="mobile-call-bar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-50 grid grid-cols-2 gap-2 p-3 bg-[#0B0D11]/95 backdrop-blur-md border-t border-white/10"
    >
      <a href={BRAND.phoneTel} data-testid="mobile-sticky-call">
        <Button className="w-full bg-brand-red hover:bg-[#c11010] text-white font-bold gap-2 h-12 rounded-xl">
          <Phone className="h-4 w-4" /> Hívás most
        </Button>
      </a>
      <button data-testid="mobile-sticky-book" onClick={() => scrollTo("foglalas")}>
        <Button variant="outline" className="w-full h-12 rounded-xl border-brand-gold/60 text-brand-gold bg-transparent hover:bg-brand-gold/10 font-bold gap-2">
          <CalendarDays className="h-4 w-4" /> Foglalás
        </Button>
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------- Hero */
function Hero() {
  return (
    <section id="hero" className="relative min-h-[92vh] flex items-center pt-20">
      <div className="absolute inset-0 bg-[#0B0D11]">
        <img
          src={REAL_PHOTOS.shop}
          alt="Ady gumiszerviz műhely belső"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 grain opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <Badge className="mb-5 bg-brand-gold/15 text-brand-gold border border-brand-gold/30 font-mono text-xs tracking-wide">
            BUDAPEST XXII. KERÜLET · ADY ENDRE ÚT 115.
          </Badge>
          <h1 className="font-display font-extrabold uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Gyors, precíz, megbízható{" "}
            <span className="text-brand-red">autó- és gumiszerviz</span> Budapesten.
          </h1>
          <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
            Transzparens árazás, gyors problémamegoldás és tételes számla gyári alkatrész-cikkszámmal.
            Reggel behozod, sokszor még aznap elkészül.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-book-btn"
              onClick={() => scrollTo("foglalas")}
              className="bg-brand-red hover:bg-[#c11010] text-white font-bold text-base h-14 px-8 rounded-full gap-2 glow-red"
            >
              <CalendarDays className="h-5 w-5" /> Időpontot foglalok
            </Button>
            <a href={BRAND.phoneTel} data-testid="hero-call-btn">
              <Button
                variant="outline"
                className="w-full sm:w-auto h-14 px-8 rounded-full border-white/25 bg-white/5 text-white hover:bg-white/10 font-bold text-base gap-2 backdrop-blur-sm"
              >
                <Phone className="h-5 w-5 text-brand-gold" /> Azonnali hívás
              </Button>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
            {[
              { icon: ReceiptText, t: "Transzparens árazás" },
              { icon: FileText, t: "Gyári cikkszámos számla" },
              { icon: Zap, t: "Azonnali diagnosztika" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-slate-200 text-sm font-medium">
                <f.icon className="h-4 w-4 text-brand-gold" /> {f.t}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- WhyUs */
function WhyUs() {
  const cards = [
    {
      icon: ShieldCheck,
      title: "Megbízhatóság",
      text: "Évek óta visszatérő ügyfelek. Amit vállalunk, azt időre és korrektül elvégezzük – felesleges munka nélkül.",
    },
    {
      icon: ReceiptText,
      title: "Transzparens árazás",
      text: "Nincs lehúzás. Unix listaárakkal dolgozunk, és a munkáról mindig tételes, pontos számlát adunk a beépített alkatrészek gyári számával.",
    },
    {
      icon: Zap,
      title: "Gyors problémamegoldás",
      text: "A hibát nem kerülgetjük. Gyors diagnosztika, célzott javítás – sok esetben pár perc alatt megoldjuk a gondot.",
    },
  ];
  return (
    <section id="rolunk" className="relative py-16 sm:py-24 bg-[#0B0D11]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="font-mono text-brand-gold text-sm mb-3 tracking-widest">// MIÉRT MINKET VÁLASSZON?</p>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            Szerelő szemlélet, ügyfélbarát hozzáállás
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <Card data-testid={`whyus-card-${i}`} className="metal-card h-full p-7 rounded-2xl">
                <div className="h-12 w-12 rounded-xl bg-brand-red/15 flex items-center justify-center mb-5">
                  <c.icon className="h-6 w-6 text-brand-red" />
                </div>
                <h3 className="font-display font-bold text-white text-xl mb-3">{c.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{c.text}</p>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          {[
            { src: REAL_PHOTOS.engine, alt: "Autó a műhelyünkben – motortér javítás" },
            { src: REAL_PHOTOS.lift, alt: "Autó az emelőn a szervizünkben" },
          ].map((img, i) => (
            <div key={i} className="relative rounded-2xl overflow-hidden border border-white/10 group">
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-56 sm:h-72 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D11] via-transparent to-transparent" />
              <span className="absolute bottom-3 left-4 text-xs font-mono text-brand-gold tracking-widest">
                A MŰHELYÜNK
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Services */
function Services() {
  return (
    <section id="szolgaltatasok" className="relative py-16 sm:py-24 bg-[#0E1116]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="font-mono text-brand-gold text-sm mb-3 tracking-widest">// SZOLGÁLTATÁSOK</p>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            Amiben segítünk
          </h2>
          <p className="mt-4 text-slate-400">
            A munkáról mindig tételes, pontos számlát adunk a beépített alkatrészek gyári cikkszámával.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Wrench;
            return (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card data-testid={`service-card-${s.key}`} className="metal-card rounded-2xl overflow-hidden h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#13171F] to-transparent" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-brand-red flex items-center justify-center glow-red">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="font-display font-bold text-white text-2xl">{s.title}</h3>
                    </div>
                  </div>
                  <div className="p-7">
                    <p className="text-slate-400 text-sm mb-5">{s.desc}</p>
                    <ul className="space-y-3">
                      {s.items.map((it, k) => (
                        <li key={k} className="flex items-start gap-3 text-slate-200 text-sm">
                          <CheckCircle2 className="h-5 w-5 text-brand-gold shrink-0 mt-0.5" />
                          {it}
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid={`service-book-${s.key}`}
                      onClick={() => scrollTo("foglalas")}
                      className="mt-6 inline-flex items-center gap-1 text-brand-red font-semibold text-sm hover:gap-2 transition-all"
                    >
                      Időpontot foglalok <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 flex items-center gap-4 metal-card rounded-2xl p-6">
          <Gauge className="h-8 w-8 text-brand-gold shrink-0" />
          <p className="text-slate-300 text-sm">
            <span className="font-semibold text-white">Bizonytalan a hibában?</span>{" "}
            Hozza be az autót diagnosztikára – megvizsgáljuk, és pontos árajánlatot adunk a javításra, mielőtt bármihez hozzáfognánk.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- Reviews */
function Reviews() {
  return (
    <section id="velemenyek" className="relative py-16 sm:py-24 bg-[#0B0D11]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <p className="font-mono text-brand-gold text-sm mb-3 tracking-widest">// ÜGYFÉLVÉLEMÉNYEK</p>
            <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
              Amit ügyfeleink mondanak
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {[...Array(5)].map((_, i) => {
              const fill = Math.max(0, Math.min(1, 4.6 - i)) * 100;
              return (
                <span key={i} className="relative inline-block h-5 w-5">
                  <Star className="absolute inset-0 h-5 w-5 text-brand-gold" />
                  <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill}%` }}>
                    <Star className="h-5 w-5 fill-brand-gold text-brand-gold" />
                  </span>
                </span>
              );
            })}
            <span className="text-white font-bold ml-1">4,6</span>
          </div>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="w-full">
          <CarouselContent className="-ml-4">
            {REVIEWS.map((r, i) => (
              <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <Card data-testid={`review-card-${i}`} className="metal-card rounded-2xl p-7 h-full flex flex-col">
                  <div className="flex gap-1 mb-4">
                    {[...Array(r.rating)].map((_, k) => (
                      <Star key={k} className="h-4 w-4 fill-brand-gold text-brand-gold" />
                    ))}
                  </div>
                  <p className="text-slate-200 text-sm leading-relaxed flex-1">„{r.text}”</p>
                  <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-brand-red/20 flex items-center justify-center font-display font-bold text-brand-red">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{r.name}</p>
                      <p className="text-slate-500 text-xs font-mono">{r.car}</p>
                    </div>
                  </div>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex gap-2 mt-8 justify-center lg:justify-start">
            <CarouselPrevious data-testid="review-prev" className="static translate-y-0 bg-white/5 border-white/15 text-white hover:bg-brand-red hover:text-white" />
            <CarouselNext data-testid="review-next" className="static translate-y-0 bg-white/5 border-white/15 text-white hover:bg-brand-red hover:text-white" />
          </div>
        </Carousel>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- Booking */
const emptyForm = { name: "", email: "", phone: "", car_type: "", service: "", problem: "" };

function Booking() {
  const [form, setForm] = useState(emptyForm);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const slots = useMemo(() => getTimeSlots(date), [date]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !date || !slot) {
      toast.error("Kérjük töltse ki a kötelező mezőket (név, telefon, szolgáltatás, dátum, időpont).");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email || null,
        date: format(date, "yyyy-MM-dd"),
        time_slot: slot,
      };
      const { data } = await api.post("/bookings", payload);
      setConfirmed({ ...data, dateLabel: format(date, "yyyy. MMMM d.", { locale: hu }) });
      toast.success("Foglalását rögzítettük!");
      setForm(emptyForm);
      setDate(null);
      setSlot("");
    } catch (err) {
      toast.error("Hiba történt a foglalás során. Kérjük hívjon minket telefonon.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="foglalas" className="relative py-16 sm:py-24 bg-[#0E1116]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-2">
            <p className="font-mono text-brand-gold text-sm mb-3 tracking-widest">// IDŐPONTFOGLALÁS</p>
            <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
              Foglaljon időpontot néhány kattintással
            </h2>
            <p className="mt-4 text-slate-400 text-sm leading-relaxed">
              Válassza ki a szolgáltatást, a dátumot és az időpontot. A foglalásról automatikus
              visszaigazolást küldünk e-mailben, majd munkatársunk telefonon megerősíti.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Clock, t: "H–Cs: 8:00–16:30 · P: 8:00–16:00", href: null, testid: "booking-info-hours" },
                { icon: MapPin, t: BRAND.address, href: BRAND.mapLink, external: true, testid: "booking-info-address" },
                { icon: Phone, t: BRAND.phoneDisplay, href: BRAND.phoneTel, testid: "booking-info-phone" },
              ].map((x, i) => {
                const inner = (
                  <>
                    <div className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-brand-gold/50 transition-colors">
                      <x.icon className="h-4 w-4 text-brand-gold" />
                    </div>
                    {x.t}
                  </>
                );
                return x.href ? (
                  <a
                    key={i}
                    href={x.href}
                    data-testid={x.testid}
                    target={x.external ? "_blank" : undefined}
                    rel={x.external ? "noopener noreferrer" : undefined}
                    className="group flex items-center gap-3 text-slate-300 hover:text-white text-sm transition-colors"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={i} className="group flex items-center gap-3 text-slate-300 text-sm">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-3">
            <Card className="metal-card rounded-2xl p-6 sm:p-8">
              <form onSubmit={submit} className="space-y-5" data-testid="booking-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Név *">
                    <Input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="Teljes név" className="bg-[#0B0D11] border-white/15" />
                  </Field>
                  <Field label="Telefonszám *">
                    <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder="+36 ..." className="bg-[#0B0D11] border-white/15" />
                  </Field>
                  <Field label="E-mail (visszaigazoláshoz)">
                    <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder="pelda@email.hu" className="bg-[#0B0D11] border-white/15" />
                  </Field>
                  <Field label="Gépjármű típusa">
                    <Input data-testid="booking-car" value={form.car_type} onChange={set("car_type")} placeholder="pl. Opel Astra 1.6" className="bg-[#0B0D11] border-white/15" />
                  </Field>
                </div>

                <Field label="Szolgáltatás *">
                  <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                    <SelectTrigger data-testid="booking-service" className="bg-[#0B0D11] border-white/15">
                      <SelectValue placeholder="Válasszon szolgáltatást" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`service-opt-${o}`}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Dátum *">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="booking-date-trigger"
                          className="w-full justify-start bg-[#0B0D11] border-white/15 text-left font-normal hover:bg-[#0B0D11] hover:text-white"
                        >
                          <CalendarDays className="mr-2 h-4 w-4 text-brand-gold" />
                          {date ? format(date, "yyyy. MM. dd.") : <span className="text-slate-500">Válasszon dátumot</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => { setDate(d); setSlot(""); }}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || isClosedDay(d)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>

                  <Field label="Időpont *">
                    <Select value={slot} onValueChange={setSlot} disabled={!date || slots.length === 0}>
                      <SelectTrigger data-testid="booking-slot" className="bg-[#0B0D11] border-white/15">
                        <SelectValue placeholder={date ? "Válasszon időpontot" : "Előbb dátum"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {slots.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`slot-opt-${s}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Probléma rövid leírása">
                  <Textarea data-testid="booking-problem" value={form.problem} onChange={set("problem")} placeholder="Írja le röviden, mi a gond az autóval..." rows={3} className="bg-[#0B0D11] border-white/15 resize-none" />
                </Field>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="w-full h-13 py-4 bg-brand-red hover:bg-[#c11010] text-white font-bold text-base rounded-xl gap-2 glow-red"
                >
                  {loading ? "Küldés..." : (<><CalendarDays className="h-5 w-5" /> Foglalás elküldése</>)}
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={!!confirmed} onOpenChange={(o) => !o && setConfirmed(null)}>
        <DialogContent data-testid="booking-confirm-dialog" className="metal-card border-white/10">
          <DialogHeader>
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center mb-2">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <DialogTitle className="text-center font-display text-2xl text-white">Foglalás rögzítve!</DialogTitle>
            <DialogDescription className="text-center text-slate-400">
              Köszönjük, {confirmed?.name}! A foglalásáról visszaigazolást küldtünk.
              Munkatársunk hamarosan telefonon megerősíti az időpontot.
            </DialogDescription>
          </DialogHeader>
          {confirmed && (
            <div className="mt-2 rounded-xl bg-[#0B0D11] border border-white/10 p-4 space-y-2 text-sm font-mono">
              <Row l="Szolgáltatás" v={confirmed.service} />
              <Row l="Dátum" v={confirmed.dateLabel} />
              <Row l="Időpont" v={confirmed.time_slot} />
              <Row l="Telefon" v={confirmed.phone} />
            </div>
          )}
          <a href={BRAND.phoneTel} className="mt-2">
            <Button variant="outline" className="w-full border-brand-gold/50 text-brand-gold bg-transparent hover:bg-brand-gold/10 gap-2">
              <Phone className="h-4 w-4" /> {BRAND.phoneDisplay}
            </Button>
          </a>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-sm">{label}</Label>
      {children}
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-slate-500">{l}</span>
      <span className="text-white text-right">{v}</span>
    </div>
  );
}

/* ----------------------------------------------------------- Contact + Map */
function Contact() {
  const todayDow = new Date().getDay();
  return (
    <section id="kapcsolat" className="relative py-16 sm:py-24 bg-[#0B0D11]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="font-mono text-brand-gold text-sm mb-3 tracking-widest">// KAPCSOLAT & TÉRKÉP</p>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            Látogasson el hozzánk
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className="metal-card rounded-2xl p-7">
              <h3 className="font-display font-bold text-white text-lg mb-5 flex items-center gap-2">
                <Clock className="h-5 w-5 text-brand-gold" /> Nyitvatartás
              </h3>
              <div className="space-y-1">
                {OPENING_HOURS.map((d) => (
                  <div
                    key={d.day}
                    className={`flex justify-between items-center py-2 px-3 rounded-lg text-sm ${
                      d.dow === todayDow ? "bg-brand-red/10 border border-brand-red/30" : ""
                    }`}
                  >
                    <span className={`font-medium ${d.dow === todayDow ? "text-white" : "text-slate-300"}`}>
                      {d.day} {d.dow === todayDow && <span className="text-brand-red text-xs font-mono ml-1">· MA</span>}
                    </span>
                    <span className={`font-mono ${d.closed ? "text-red-400" : "text-slate-200"}`}>{d.hours}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="metal-card rounded-2xl p-7 space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-brand-gold shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-500 text-xs font-mono uppercase">Cím</p>
                  <p className="text-white font-medium">{BRAND.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-brand-gold shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-500 text-xs font-mono uppercase">Telefon</p>
                  <a href={BRAND.phoneTel} data-testid="contact-phone" className="text-white font-medium hover:text-brand-red transition-colors">
                    {BRAND.phoneDisplay}
                  </a>
                </div>
              </div>
              <a href={BRAND.phoneTel}>
                <Button className="w-full bg-brand-red hover:bg-[#c11010] text-white font-bold gap-2 rounded-xl mt-2 glow-red">
                  <Phone className="h-4 w-4" /> Hívás most
                </Button>
              </a>
            </Card>
          </div>

          <div className="rounded-2xl overflow-hidden border border-white/10 min-h-[400px] h-full">
            <iframe
              title="Térkép"
              data-testid="google-map"
              src={BRAND.mapEmbed}
              className="w-full h-full min-h-[400px]"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ Footer */
function Footer() {
  return (
    <footer className="bg-[#08090C] border-t border-white/10 pb-24 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt={BRAND.name} className="h-12 w-12 rounded-full" />
              <span className="font-display font-extrabold text-white text-lg">
                Ady <span className="text-brand-red">Szerviz</span>
              </span>
            </div>
            <p className="text-slate-500 text-sm leading-relaxed">
              Autó- és gumiszerviz Budapest XXII. kerületében. Gyors, precíz, megbízható munka, transzparens árazással.
            </p>
          </div>
          <div>
            <h4 className="font-display font-bold text-white mb-4">Elérhetőség</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-gold" /> {BRAND.address}</li>
              <li><a href={BRAND.phoneTel} className="flex items-center gap-2 hover:text-white"><Phone className="h-4 w-4 text-brand-gold" /> {BRAND.phoneDisplay}</a></li>
              <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-brand-gold" /> H–Cs: 8–16:30 · P: 8–16</li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold text-white mb-4">Menü</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button onClick={() => scrollTo(l.id)} className="hover:text-white">{l.label}</button>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-slate-600 text-xs">© {new Date().getFullYear()} {BRAND.name}. Minden jog fenntartva.</p>
          <a href="/admin/login" data-testid="footer-admin-link" className="text-slate-600 text-xs hover:text-slate-400">Admin belépés</a>
        </div>
      </div>
    </footer>
  );
}

export default function Home() {
  return (
    <div className="bg-[#0B0D11] min-h-screen">
      <Navbar />
      <Hero />
      <WhyUs />
      <Services />
      <Reviews />
      <Booking />
      <Contact />
      <Footer />
      <MobileCallBar />
    </div>
  );
}
